/*
<purpose>The pure slot model behind a triage row's drafts: which of the draft slots the row
offers, which of them carry text, which are only offered, and what shape the row is in — plus the
German vocabulary for naming a kind and a claim state.</purpose>
<dependencies>types.ts (wire DTOs) · services/api.ts (RENDER_SOURCE_TRIAGE_DRAFT, re-exported below) ·
services/categoryMeta.ts (FaceFn — every kind-word function below takes a face resolver rather than
reading a code map; part 4c, 2026-09-27).</dependencies>
<constraints>
  - Pure. No React, no network, no formatting of anything but the two labels. Every draft surface
    reads THIS, so a rule lives here once instead of in each panel.
  - The slot array is a PRE-ALLOCATION: every draft kind is always present whatever the row
    carries, so a surface built on it never reflows when a draft appears or is claimed.
  - ORDER (operator ruling 2026-09-08): the KI's own proposal first — the slot whose posture is
    `primary` — then the alternatives in the order the TENANT's primary axis is served in
    (`/config`'s value list, mapped kind -> handling through the served `decisionKindHandling`). A
    row with no proposal is laid out by the served order alone. The order is therefore a function of SERVED
    DATA plus the row's own postures — never of what the clerk pressed — so positional stability
    per ticket holds: the same row lays out identically on every visit, and `rendered` and `lazy`
    are the same order as `slots`. The 2026-09-07 rule this replaces was a fixed [reply, forward];
    it kept positions stable but buried the pipeline's own proposal below an alternative.
  - RECEIPT (2026-09-17): the Empfangsbestätigung is not a draft ROW — it is stateless, composed
    fresh by the detail read and handed over on the detail itself (`TicketDetail.receipt`). So the
    ack slot is filled from THAT, and the lazy strip survives only for a core that does not serve
    it. A surface that waited for an ack draft slot waited for something that is never written.
  - RECEIPT RESTS COLLAPSED (C3.6, ruled 2026-09-16): a served receipt is real text — composed by
    the detail read — but the operator ruled it stands as a companion STRIP at rest, never an
    already-open card; a press reveals the same card instantly since the text is already in hand.
    The slot carries `restsCollapsed: true` for this, and `rendered`/`lazy` read that flag rather
    than `text !== null` alone, so a surface walking those two lists needs no third rule for "has
    text but still a strip".
</constraints>
*/

import type {
  Candidate, DraftClaim, DraftKind, DraftPosture, ScaffoldData, SuggestionInfo, TicketDetail, TicketReceipt,
} from '../serverTypes';
import { WIM_HANDLING } from '../categories';
import { RENDER_SOURCE_TRIAGE_DRAFT } from './api';
import type { FaceFn } from './categoryMeta';
// The render source is a WIRE constant, so it lives with the other wire constants in api.ts (the
// one file adjudicated to carry the pipeline's route token) and is re-exported here — draft
// surfaces read the whole model vocabulary from this module, not from the API client. Re-exported
// from the imported binding rather than as `export … from` so both statements stay in the import
// block (import/first — the rule only bit once an app module pulled this file into the build).
export { RENDER_SOURCE_TRIAGE_DRAFT };

// The kinds that are DRAFTS — a composed text the clerk can read, copy and hand on. The receipt
// joined them on 2026-09-08 (operator ruling): it is composed from the tenant's own receipt
// template exactly like the other two, so it is an ALTERNATIVE in the slot walk rather than a bar
// action. 'archive' remains the one claimable kind with no artifact behind it, so it never occupies
// a slot.
// 'internal' joined on 2026-09-16 (P3 v2): the Auskunft letter, written for the colleague who
// asked WIM to look something up. It needed no rule of its own in the walk either — it is a
// slot like the other three, offered by its own posture and composed by its own strip.
export type DraftSlotKind = 'reply' | 'forward' | 'ack' | 'internal';

// The kinds the bottom bar can SETTLE. Deliberately narrower than DraftSlotKind: the receipt is
// claimed by COPYING it (copy = indirect approval), never by an approve button, so no derivation on
// that bar may read its slot — see services/pressBar.ts. Re-ruled and tightened on
// 2026-09-17: core now REFUSES `claim_ack` with via=approve (400 receipt_is_copy_only), so the
// exclusion below is the FE half of a contract both ends keep. The FE does not depend on that
// refusal — it simply never offers the gesture. Declared here, beside the slot
// kinds themselves, so "which kinds are decisions" is one fact in the vocabulary module rather than
// a list re-spelled on the surface that happens to need it.
// A TYPE as well as a list (Part C walk round, 2026-09-09): the bar now derives its slot ORDER by
// walking model.slots and keeping the decision kinds, and each surviving kind indexes a per-kind
// reserved width. Typed narrowly, that lookup is total by construction — a fourth draft kind
// arriving would fail to compile at the width table rather than at runtime in a clerk's bar.
// The Auskunft letter joined on 2026-09-16 (P3 v2 Task 6b), once core could settle it: it is
// approved, downloaded and rewritten exactly as the other two letters are. The receipt still is
// not — it is claimed by COPYING it, never by an approve button.
export type DraftDecisionKind = 'reply' | 'forward' | 'internal';
export const DRAFT_DECISION_KINDS: readonly DraftDecisionKind[] = ['reply', 'forward', 'internal'];

/**
 * The kinds a clerk can REWRITE as free text — the FE mirror of core's own `EditableKinds`
 * (Functions/Suggestion/SecondaryDraftFunctions.cs), which is the contract that decides it. The
 * receipt is refused there because a fixed notice is not a draft — it is the only kind refused,
 * and the only reason it is a list at all. Declared here rather than as a `!== 'ack'` exception
 * on the card, so the ONE question "may this kind be rewritten" has one answer in the vocabulary
 * module — and extending core's list is a one-line edit HERE, at a name that says what it mirrors.
 */
export type DraftEditableKind = 'reply' | 'forward' | 'internal';
export const DRAFT_EDITABLE_KINDS: readonly DraftEditableKind[] = ['reply', 'forward', 'internal'];

/** Whether core will accept a free-text rewrite of this kind — see DRAFT_EDITABLE_KINDS. */
export const isDraftEditableKind = (kind: DraftSlotKind): kind is DraftEditableKind =>
  DRAFT_EDITABLE_KINDS.some(editable => editable === kind);

/**
 * The ticket statuses in which a triage decision is still LIVE — the row is on a decision surface
 * and its drafts can still be composed, rewritten and claimed. Every other status (archived above
 * all) is read-only for triage: the cards and the panel are information there.
 *
 * Declared HERE, in the vocabulary module both the draft area and both hosts already read, because
 * three surfaces answer this same question and were answering it differently (walk fix round 6,
 * 2026-09-09): the draft area allowed all three statuses for its offer strips and Bearbeiten, while
 * each host's copy handler allowed only `suggestion_ready`. So a clerk could rewrite a draft on a
 * Genehmigt row but copying it recorded nothing — and core never agreed with the narrow reading in
 * the first place: its claim executor carries NO ticket-status gate, only single-use per kind.
 * Operator ruling: a row in Genehmigt is settled about ONE kind, and copying the other draft is a
 * real second decision that must be recorded.
 */
export const TRIAGE_DECIDABLE_STATUSES: ReadonlySet<string> =
  new Set(['suggestion_ready', 'approved', 'sent']);

/** Whether a triage decision is still live on this row — see TRIAGE_DECIDABLE_STATUSES. */
export const isTriageDecidable = (status: string | null | undefined): boolean =>
  !!status && TRIAGE_DECIDABLE_STATUSES.has(status);

// Two questions with near-identical names, kept side by side so a reader who finds one finds both:
// - isDraftDecisionKind asks about a LETTER SLOT on the bottom bar: reply, forward and internal can
//   be settled there; the receipt is copied, never approved.
// - isDecisionClaimKind asks about a CLAIM on a row: every claimed kind except the receipt counts as
//   the clerk's decision, `archive` (the no-letter claim) included. The receipt (R31, 2026-09-18) is
//   a companion artifact: no decision badge, no decision date. The row (components/TicketRowMeta.tsx)
//   and the detail's claim-timestamp resolver both read this one rule.

/** Whether a slot kind is one the bottom bar can settle — the narrowing form of the list above. */
export const isDraftDecisionKind = (kind: DraftSlotKind): kind is DraftDecisionKind =>
  DRAFT_DECISION_KINDS.some(decision => decision === kind);

/** Whether a claimed kind counts as the clerk's decision (every kind but the receipt). */
export const isDecisionClaimKind = (kind: DraftKind): boolean => kind !== 'ack';

// The layout of last resort: what the slots read as when the tenant serves no primary axis at all
// (and while the very first /config fetch is still in flight). NOT a preference — it is simply the
// order this file shipped with, kept so a tenant without a served vocabulary lays out as it always
// has rather than by an invented rule. The receipt is appended rather than woven in, for the same
// reason: it is the newest kind, and a tenant with no served order has said nothing that would move
// it up.
const DEFAULT_KIND_ORDER: readonly DraftSlotKind[] = ['reply', 'forward', 'ack', 'internal'];

/**
 * The tenant's OWN order for the draft slots, derived from the served primary-axis values
 * (`useCategoryMeta().categoryKeys` — the served ids in served order) mapped through `kindHandling`
 * (`useCategoryMeta().decisionKindHandling`, core's served `/config` field — part 4c, 2026-09-27;
 * the code constant `CLAIM_KIND_HANDLING` this used to read is deleted). Kinds the tenant does not
 * serve keep their slots and are appended in the default order — a slot is a pre-allocation, so it
 * is never dropped for being unserved. Nothing served (or nothing served that resolves to a draft
 * kind, including an EMPTY `kindHandling` — a generation-9 backend that predates the field) falls
 * back to the historical layout, never a guessed one.
 * Pure and exported so the host can derive it ONCE per render and hand the same array to the model
 * (2026-09-08).
 */
export function draftKindOrder(
  primaryAxisValues: readonly string[] | null | undefined,
  kindHandling: Readonly<Record<string, string>>,
): DraftSlotKind[] {
  const ordered: DraftSlotKind[] = [];
  for (const value of primaryAxisValues ?? []) {
    for (const kind of DEFAULT_KIND_ORDER) {
      if (kindHandling[kind] === value && !ordered.includes(kind)) ordered.push(kind);
    }
  }
  for (const kind of DEFAULT_KIND_ORDER) if (!ordered.includes(kind)) ordered.push(kind);
  return ordered;
}

/**
 * The kinds a row has been CLAIMED for, as the flat list the decision badges take (R32,
 * 2026-09-18). The DETAIL's claim shape is a keyed map (`suggestion.claims`) while the LIST ROW's
 * is already a flat array (`draftClaims`); this is the one conversion between them, so the header
 * and the row can mount the identical component instead of each deriving a list of its own.
 *
 * Order is the SERVED one — JSON object keys preserve their insertion order, which is core's own
 * emission order and therefore the same order the list row's array arrives in. Deliberately NOT
 * re-sorted through draftKindOrder: that is the SLOT layout (which box sits where on the stage),
 * and re-imposing it here would make the detail state the decisions in a different order from the
 * row the clerk clicked — the exact drift this helper exists to prevent.
 */
export function claimedKinds(claims: Partial<Record<DraftKind, DraftClaim>> | null | undefined): string[] {
  return claims ? Object.keys(claims) : [];
}

/**
 * The claimed kinds that are DECISIONS — claimedKinds minus the receipt (isDecisionClaimKind, whose
 * ruling lives in constants.ts). The detail header asks this before
 * it draws its decision LINE at all: a ticket whose only claim is a receipt has decided nothing, so
 * the line must not stand there empty with its own gap and margin.
 */
export function decisionKinds(claims: Partial<Record<DraftKind, DraftClaim>> | null | undefined): string[] {
  return claimedKinds(claims).filter(kind => isDecisionClaimKind(kind as DraftKind));
}

/**
 * WHEN this ticket was DECIDED — the stamp the DETAIL's decision line dates the past tense with:
 * `suggestion.claims[kind].confirmedAt`, the claim's own moment. The FIRST decision claim's, in
 * served order, which is the same order the badges render in, so the date belongs to the badge
 * that leads the group rather than to an arbitrary one of them.
 *
 * Answers null rather than a guess. A claimed ticket whose stamp never arrived (a pre-stamp row,
 * or a backend that does not serve one) states the past tense UNDATED — an invented date would be
 * a fact about the clerk's work that nobody recorded.
 *
 * ⚠ Its LIST-ROW twin (`rowDecisionAt`, `approvedAt ?? copyApprovedAt`) is DELETED, not disabled:
 * ruling R33 (2026-09-19) took the decision date off the rows altogether, and the detail is the one
 * surface that dates a decision now. A resolver kept for a surface that no longer asks is the dead
 * code this codebase does not keep.
 */
export function decisionClaimAt(claims: Partial<Record<DraftKind, DraftClaim>> | null | undefined): string | null {
  if (!claims) return null;
  for (const [kind, claim] of Object.entries(claims)) {
    if (!isDecisionClaimKind(kind as DraftKind)) continue;
    if (claim?.confirmedAt) return claim.confirmedAt;
  }
  return null;
}

export interface DraftSlot {
  kind: DraftSlotKind;
  posture: DraftPosture;
  text: string | null;
  renderSource?: string | null;
  /**
   * When a clerk last rewrote THIS slot's draft; null when it was never edited (and on every slot
   * that owns no artifact). Follows the artifact that owns the slot, exactly as `text` and
   * `renderSource` do — so the stamp is per SLOT and one kind's edit can never light the pill on
   * the kind beside it. Both sources are TYPED wire fields (2026-09-09); nothing here reads a
   * scaffold.
   */
  editedAt: string | null;
  /**
   * The LETTER behind this slot, when the composer built one from segments — the same
   * `ScaffoldData` the VNB letter flow fills in, folded per slot from the artifact that owns it
   * (the suggestion's own parsed envelope for the primary, the secondary's raw `scaffoldJson` for
   * the other). null when the slot owns no artifact, when the envelope carries no segments (the
   * receipt), and on every row whose draft is plain text.
   *
   * It lives on the SLOT for the same reason `text` and `editedAt` do: the surface that renders a
   * slot must not have to know which of the two artifacts it came from. A scaffold with an open
   * slot segment is a letter with a GAP in it — a system fact the model was forbidden to invent —
   * and that is what mounts the fill-in pieces and holds Kopieren back.
   */
  scaffold: ScaffoldData | null;
  claim: DraftClaim | null;
  /**
   * True when a SERVED artifact still rests behind a companion strip rather than standing open as
   * a card — `text`/`renderSource`/`scaffold` above are already the real thing, this flag says only
   * that the surface has not been asked to show it yet.
   *
   * The receipt's alone at first (C3.6, ruled 2026-09-16); since the eyeball round of 2026-09-18 it
   * is EVERY slot but the row's own proposal. False on a slot with no text at all — there is
   * nothing behind that strip to disclose, and it is a compose offer rather than a resting letter.
   */
  restsCollapsed?: boolean;
}

/**
 * A secondary draft — and the detail's own receipt — carries its envelope as the RAW string core
 * stored, where the primary's has already been parsed into the DTO. Exported since 2026-09-17: the
 * draft area reveals a freshly composed draft from the render RESPONSE, whose envelope arrives in
 * the same raw shape, and a second parser would be a second set of rules.
 * Tolerant by construction: an envelope that will not parse, or
 * that carries no segment array (the receipt's own shape), is simply "no letter to fill in" — the
 * slot renders its plain text, which is the same thing every pre-scaffold row does. A scaffold is
 * never a reason to fail a fold.
 */
export const parseScaffoldJson = (json: string | null | undefined): ScaffoldData | null => {
  if (typeof json !== 'string' || json.length === 0) return null;
  try {
    const parsed = JSON.parse(json);
    const segments = parsed?.segments;
    return Array.isArray(segments) && segments.length > 0 ? { segments } : null;
  } catch {
    return null;
  }
};

/** The parsed envelope as served, or nothing — the primary half of the rule above. */
const servedScaffold = (scaffold: ScaffoldData | null | undefined): ScaffoldData | null =>
  scaffold && Array.isArray(scaffold.segments) && scaffold.segments.length > 0 ? scaffold : null;

export interface DraftModel {
  /**
   * Every draft kind, always — the pre-allocation the surfaces lay out against. Ordered
   * proposal-first, then the served primary-axis order (see the file header's ORDER rule).
   */
  slots: DraftSlot[];
  /** Slots that carry text, in SLOT order — the same order as `slots`, never a second rule. */
  rendered: DraftSlot[];
  /** Slots offered but not composed yet — the on-demand strips, in SLOT order. */
  lazy: DraftSlot[];
  /** The kind the row's POSTURES make primary; null when the row offers no primary (nothing to
   *  reply to, or a Prüfen row, which declares none). */
  primaryKind: DraftSlotKind | null;
  /**
   * The kind the walk LEADS with (R38, part 5-2, 2026-10-02): the posture primary where there is
   * one, else the classifier's lean on a row whose Begründung states it (see leanKindOf) — and
   * only when the row OFFERS that kind (a posture other than `none`, or a composed letter). The
   * strip walk draws this kind's strip as the primary strip; null = nothing leads.
   */
  leadKind: DraftSlotKind | null;
  /**
   * drafts  = the row has something to show or to offer
   * legacy  = a row from before the posture envelope existed (no postures, no text)
   * refusal = the composer was asked for the primary and returned nothing
   * none    = the row deliberately offers no draft at all
   */
  shape: 'drafts' | 'legacy' | 'refusal' | 'none';
  claims: Partial<Record<DraftKind, DraftClaim>>;
  /**
   * The row's SERVED triage handling is the review handling (Prüfen), whatever its decision source
   * (R42, part 5-2, 2026-10-06). Read off `suggestion.triage.handling` — the record core persisted —
   * never off the status, a posture pattern or a label: a Prüfen row's postures look exactly like
   * any other no-primary row's, so only the handling itself says which it is. The bottom bar reads
   * this to give an open Prüfen row its third act, „Ablegen genehmigen" (filing the row as
   * Genehmigt without a letter). False on every row without a triage record.
   */
  inReview: boolean;
}

const nonEmpty = (text: string | null | undefined): text is string =>
  typeof text === 'string' && text.trim().length > 0;

/**
 * Folds a suggestion row and the ticket's draft postures into the model every draft surface reads.
 * Tolerates both absent halves: a ticket with no suggestion and no postures is a legacy row, not a
 * crash and not an empty offer.
 *
 * `kindOrder` is the TENANT's slot order (draftKindOrder above), derived once by the host from the
 * served primary axis. Omitted — a caller with no vocabulary in hand, and every pre-2026-09-08 test
 * double — reads as the default layout, never as "no slots".
 *
 * `receipt` is the DETAIL READ's own Empfangsbestätigung (types.ts TicketReceipt, 2026-09-17).
 * Present ⇒ it FILLS the ack slot: the receipt is stateless, so it never arrives as a draft row and
 * a slot waiting for one waits forever. Absent/null ⇒ the ack slot is whatever the postures and the
 * artifacts say it is, which is exactly the pre-2026-09-17 reading and what an older core still
 * serves.
 */
export function buildDraftModel(
  suggestion: SuggestionInfo | null | undefined,
  postures: TicketDetail['draftPostures'],
  kindOrder?: readonly DraftSlotKind[] | null,
  receipt?: TicketReceipt | null,
  leanKind?: DraftSlotKind | null,
): DraftModel {
  const claims = suggestion?.claims ?? {};
  const secondary = suggestion?.secondary ?? null;
  // The served order first, then the row's own proposal lifted to the front. Both halves are
  // data — the served vocabulary and the row's postures — so this order is stable for a ticket
  // across visits and across clicks; only a re-classification can change it.
  const servedOrder = kindOrder?.length ? kindOrder : DEFAULT_KIND_ORDER;
  const proposed = servedOrder.find(kind => (postures?.[kind] ?? 'none') === 'primary') ?? null;
  // R38 (part 5-2, 2026-10-02): a row with NO posture primary whose Begründung is served leads with
  // the BLOCK, not with a letter — every composed letter rests behind its strip, and the
  // classifier's lean (when the Begründung states one) leads the walk. Data again: the reasoning and
  // the lean are on the row, so the layout is the same on every visit.
  // …but only a lean the row can ACT on (final review, Important 2): the lean's kind leads only
  // when that kind is offered here — a posture other than `none`, or a letter already composed for
  // it (the row's own artifact, the secondary, or the served receipt). An Unklassifiziert row whose
  // Begründung states a lean while every posture is `none` has nothing of that kind to show, so it
  // has no lead at all: the bar's lean-led weighting stays off and the filing button keeps its
  // filled face. The lean LINE in the block is untouched — it is the classifier's answer either way.
  const offered = (kind: DraftSlotKind): boolean =>
    (postures?.[kind] ?? 'none') !== 'none'
    || (kind === 'ack' && !!receipt)
    || (suggestion?.artifactKind === kind && nonEmpty(suggestion?.suggestionText))
    || (secondary?.artifactKind === kind && nonEmpty(secondary?.text));
  const restsAll = proposed === null && !!suggestion?.reasoning;
  const lead = proposed ?? (restsAll && leanKind && offered(leanKind) ? leanKind : null);
  let order: readonly DraftSlotKind[] = lead
    ? [lead, ...servedOrder.filter(kind => kind !== lead)]
    : servedOrder;

  // The receipt is a COMPANION to the primary, not an alternative (operator ruling 2026-09-17,
  // C3.6): when a row leads with a primary AND still offers the receipt as a lazy strip, that
  // strip renders immediately under the primary card — never buried behind the other
  // alternatives, which keep their existing relative order. Guarded on the ACTUAL posture, never assumed: a row with
  // no receipt offered, or one composed AS the receipt (proposed === 'ack'), is untouched.
  // Since 2026-09-17 a SERVED receipt lifts the slot the same way an offered one does: the card is
  // the companion, and it sits where the strip used to — under the primary, whatever the posture
  // says. (A core serving the receipt may well leave the posture lazy; the read is what decides.)
  const offersReceipt = !!receipt || (postures?.ack ?? 'none') === 'lazy';
  if (proposed && proposed !== 'ack' && offersReceipt) {
    order = [proposed, 'ack', ...order.filter(kind => kind !== proposed && kind !== 'ack')];
  }

  const slots: DraftSlot[] = order.map((kind) => {
    // The served receipt OUTRANKS both artifacts on its own slot: it is the live composition the
    // detail read just returned, where an ack draft row (if one even exists on this row) is a
    // leftover from before the receipt went stateless. One kind, one source of truth.
    if (kind === 'ack' && receipt) {
      return {
        kind,
        posture: postures?.ack ?? 'none',
        text: receipt.text,
        renderSource: receipt.renderSource,
        // The receipt is the one kind core refuses to rewrite (DRAFT_EDITABLE_KINDS), so there is
        // no edit stamp to read and nothing to invent one from.
        editedAt: null,
        scaffold: parseScaffoldJson(receipt.scaffoldJson),
        claim: claims.ack ?? null,
        // Real text, but resting (see the type's own doc) — C3.6.
        restsCollapsed: true,
      };
    }

    const isPrimaryArtifact = suggestion?.artifactKind === kind;
    const isSecondaryArtifact = secondary?.artifactKind === kind;

    const text = isPrimaryArtifact && nonEmpty(suggestion?.suggestionText)
      ? suggestion!.suggestionText
      : isSecondaryArtifact && nonEmpty(secondary?.text)
        ? secondary!.text
        : null;

    // Provenance follows the artifact that OWNS the slot, even when its text came back empty —
    // that pairing (empty text + this render source) is exactly what identifies a refusal.
    const renderSource = isPrimaryArtifact
      ? suggestion?.renderSource ?? null
      : isSecondaryArtifact
        ? secondary?.renderSource ?? null
        : null;

    // Same ownership rule as `text` above: the row's own artifact reads the suggestion's stamp, the
    // secondary artifact reads its own, and a slot that owns neither was never edited.
    const editedAt = isPrimaryArtifact
      ? suggestion?.draftEditedAt ?? null
      : isSecondaryArtifact
        ? secondary?.editedAt ?? null
        : null;

    // Same ownership rule once more: the letter follows the artifact that owns the slot, so one
    // kind's gaps can never be read off the kind beside it.
    const scaffold = isPrimaryArtifact
      ? servedScaffold(suggestion?.scaffold)
      : isSecondaryArtifact
        ? parseScaffoldJson(secondary?.scaffoldJson)
        : null;

    return {
      kind,
      posture: postures?.[kind] ?? 'none',
      text,
      renderSource,
      editedAt,
      scaffold,
      claim: claims[kind] ?? null,
      // EVERY non-primary artifact rests (eyeball round 4, ruled 2026-09-18) — the row's own
      // proposal stands open and nothing else does. This flag used to be the receipt's alone, so a
      // row whose alternative had been composed in an EARLIER session opened with two full letters
      // stacked on it: the alternative was a card purely because its text was not null, which is a
      // fact about the composer's history rather than about what the clerk is deciding now.
      // Disclosure decides from here (services/draftDisclosure), and composing already marks the
      // kind disclosed, so a letter the clerk just asked for still opens immediately.
      //
      // `proposed !== null` is load-bearing, not a null-guard: a row where NO posture is primary
      // has no proposal to stand open, and resting everything there would leave a row holding two
      // composed letters with neither of them shown. Non-primary is a position relative to a
      // primary that exists.
      //
      // R38 widens it once: a row with a Begründung and no posture primary (`restsAll` — Prüfen)
      // rests EVERY letter, the lean's included. The block is the open thing on that row; the two
      // letters wait behind their strips, and neither is shown before the clerk asks.
      restsCollapsed: text !== null && (restsAll || (proposed !== null && kind !== proposed)),
    };
  });

  // ONE order for all three lists (2026-09-08): whatever `slots` says, `rendered` and `lazy` say
  // too. The deduplicated row-order walk this replaced was correct about what the row composed
  // first and wrong about where a clerk should find it — the proposal now leads the slots
  // themselves, so no surface needs a second ordering rule to state the row's emphasis.
  // A slot resting collapsed (the served receipt) carries real text but is NOT a rendered card —
  // it is read into `lazy` beside the never-composed strips, which is exactly the shape a surface
  // walking these two lists needs: one flag, read once, here.
  const rendered = slots.filter(slot => slot.text !== null && !slot.restsCollapsed);
  const lazy = slots.filter(slot =>
    slot.restsCollapsed || (slot.posture === 'lazy' && slot.text === null));
  const primaryKind = proposed;

  // A refusal outranks the 'drafts' reading: a row whose PRIMARY came back empty from the triage
  // composer has a problem to state, even when the other slot is still offering a lazy strip.
  const refused = slots.some(slot =>
    slot.posture === 'primary' && slot.text === null && slot.renderSource === RENDER_SOURCE_TRIAGE_DRAFT);

  const shape: DraftModel['shape'] = refused
    ? 'refusal'
    : rendered.length > 0 || lazy.length > 0
      ? 'drafts'
      : postures
        ? 'none'
        : 'legacy';

  const inReview = suggestion?.triage?.handling === WIM_HANDLING.review;

  return { slots, rendered, lazy, primaryKind, leadKind: lead, shape, claims, inReview };
}

/**
 * The letter kind the row LEADS with when a flag moved the landing (R38; part 6, 2026-10-06): the
 * first ranked candidate (`assessment.candidates`) whose value is not the landed handling, mapped
 * back to the draft kind whose served handling it is (`decisionKindHandling`, kind -> handling),
 * letter kinds only. Null when that candidate is no letter's handling (Zur Kenntnis), when the landed
 * handling is the only candidate, or when nothing is served to map through. Rows written before the
 * candidates were served carry no array or an empty one: those keep the earlier reading (the classifier's
 * own answer, `classifierPrimary`, counted only when the Begründung states a lean).
 * The receipt and the archive are never a lead's letter — neither is a decision kind.
 */
export function leanKindOf(
  suggestion: SuggestionInfo | null | undefined,
  kindHandling: Readonly<Record<string, string>>,
): DraftSlotKind | null {
  const candidates = suggestion?.assessment?.candidates;
  if (candidates?.length) {
    const alt = candidates.find(c => c.value !== suggestion?.triage?.handling);
    return alt ? DRAFT_DECISION_KINDS.find(kind => kindHandling[kind] === alt.value) ?? null : null;
  }
  const answer = suggestion?.assessment?.classifierPrimary;
  if (!answer || !suggestion?.reasoning?.lean) return null;
  return DRAFT_DECISION_KINDS.find(kind => kindHandling[kind] === answer) ?? null;
}

/** The ranked candidate whose value is this kind's handling (the lead included) — what a strip's
 * hover explains. Null when the row carries no candidates or none maps to the kind. */
export function candidateForKind(
  suggestion: SuggestionInfo | null | undefined,
  kindHandling: Readonly<Record<string, string>>,
  kind: DraftSlotKind,
): Candidate | null {
  // Letter kinds only: the receipt's served handling is Zur Kenntnis's, so a lookup by it would hand
  // the receipt strip the Zur Kenntnis candidate's reason.
  if (kind === 'ack') return null;
  const handling = kindHandling[kind];
  if (!handling) return null;
  return suggestion?.assessment?.candidates?.find(c => c.value === handling) ?? null;
}

// ── Every user-facing spelling of a kind, from the SERVED `draft_kind` face family (part 4c,
// 2026-09-27) ─────────────────────────────────────────────────────────────────────────────────
// The code-owned KIND_WORD / KIND_OFFER_WORD / ACK_OFFER_LABEL maps this block used to declare are
// deleted outright; every function below takes the caller's `face` (`useCategoryMeta().face` at
// every real call site) and reads the tenant's `draft_kind` face family instead.
// `resolveFace` already floors an unserved family or key to the raw KEY — never a code word — so
// `word` only has to fall back to `kind` itself for the one thing resolveFace CAN answer that a bar
// button or card heading cannot honour: an explicit served null (§13.4, "do not put this on a
// row").
// A served word is NEVER inflected (operator ruling, part 4c, 2026-09-27 — the „Direkte Antwort" /
// „Direkt antworten" reword that retired the last two exceptions to this): every suffix below wraps
// the served word in fixed German ("(direkt)", "an <Einheit>", "an Kollegen", "genehmigen",
// "herunterladen") rather than declining it, so a tenant can re-word a kind to anything without the
// German case system mangling it.
const word = (kind: DraftKind, face: FaceFn) => face('draft_kind', kind) ?? kind;

/** German one-word name of a draft/action kind, for buttons and inline offers. */
export function draftKindWord(kind: DraftKind, face: FaceFn): string { return word(kind, face); }

/** Heading form. Never inflects the served word (operator ruling, part 4c, 2026-09-27):
 * „<Antwort> (direkt)", „<Weiterleitung> an <Einheit>". No recipient rides the reply heading —
 * "an den Kunden" was the only recipient this UI ever knew, so naming it added a word without
 * adding an answer. `ack`/`archive`/`internal` have no heading suffix: the served word IS the
 * heading, spelled once here rather than repeated per kind. */
export function draftKindLabel(kind: DraftKind, face: FaceFn, targetUnit?: string | null): string {
  const w = word(kind, face);
  if (kind === 'reply') return `${w} (direkt)`;
  if (kind === 'forward') return targetUnit ? `${w} an ${targetUnit}` : w;
  return w;
}

// The offer word — how the on-demand strip names the alternative it can compose. It is the
// GESTURE, not the artifact, because the strip is a thing to press; the heading beside it names
// the artifact. Only reply and internal diverge from the plain word: the strip names the RECIPIENT
// for internal where the heading does not, because „Auskunft" alone reads as a classification
// rather than as a letter to compose, and the one thing a clerk needs to know before pressing is
// who it would be written to.
const offerWord = (kind: DraftSlotKind, face: FaceFn) =>
  kind === 'reply' ? `${word(kind, face)} (direkt)` : kind === 'internal' ? `${word(kind, face)} an Kollegen` : word(kind, face);

/** The bottom bar's approval verb: what pressing the slot decides. */
export function draftKindActionLabel(kind: DraftKind, face: FaceFn): string { return `${word(kind, face)} genehmigen`; }

/** The bottom bar's execution verb: what comes out of an approved kind. */
export function draftKindDownloadLabel(kind: DraftSlotKind, face: FaceFn): string { return `${word(kind, face)} herunterladen`; }

/** The on-demand strip's own line, with no prefix word (the strip's weight says which strip
 * leads, the Begründung says why): „<Kind> — Entwurf erzeugen". The receipt keeps its companion
 * wording (operator ruling 2026-09-17, C3.6) — a COMPANION to whatever the row leads with, never
 * an alternative to it — and names the customer as its recipient, the one thing the served word
 * would otherwise leave unsaid. */
export function draftKindOfferLabel(kind: DraftSlotKind, face: FaceFn): string {
  return kind === 'ack' ? `${word(kind, face)} an den Absender — Entwurf anzeigen` : `${offerWord(kind, face)} — Entwurf erzeugen`;
}

/**
 * WHICH served face a recorded claim asks for; null when there is no claim to state. Pure — no
 * face lookup here, only the `via` -> key decision (see claimStateLabel below for the reason this
 * split exists: a render site that must show the loading/served-null states honestly (ServedFace,
 * TicketRowMeta.tsx) needs the (family, key) pair on its own, before any face function ever runs).
 *
 * For the RECEIPT only the `copy` key is reachable (operator ruling 2026-09-17): its sign-off is
 * the template pin, not a clerk's approval, so no surface offers an approval word for it and core
 * refuses `claim_ack` with via=approve outright. Stated here rather than branched on `kind`,
 * deliberately — this function names what the server recorded, and a kind-shaped exception would
 * mean relabelling a claim the server says exists.
 */
export function claimFaceKey(claim: DraftClaim | null | undefined): { family: string; key: string } | null {
  if (!claim) return null;
  return { family: 'decision', key: claim.via === 'copy' ? 'copy' : 'approve' };
}

/**
 * What a recorded claim reads as; null when there is no claim to state OR the tenant served an
 * explicit null for this face (§13.4 — "do not put this on a row"; resolveFace's return type is
 * honest about this, see categoryMeta.ts, final review finding 2).
 *
 * The word is a SERVED FACE (P4b, part of the badge-faces round): claimFaceKey above decides WHICH
 * key to ask for, and the caller's `face` function — `useCategoryMeta().face`, everywhere this is
 * actually called — turns that into the tenant's own word for the `decision` family. A tenant
 * re-wording its vocabulary changes what this reads as without a line here changing.
 *
 * A render site that must show the LOADING state honestly (a shimmer, never the raw key) does not
 * call this directly — it goes through ServedFace.tsx using claimFaceKey instead, since
 * this function has no way to say "not yet known" (its `face` parameter is assumed already
 * trustworthy). This function stays useful wherever a caller already knows the vocabulary is
 * settled and just wants the one word.
 */
export function claimStateLabel(
  claim: DraftClaim | null | undefined,
  face: FaceFn,
): string | null {
  const k = claimFaceKey(claim);
  return k ? face(k.family, k.key) : null;
}

// ── A letter's open gaps: the one count, and the one sentence about it ──────────────────────────
// A slot segment the server still serves as unfilled is a GAP — a fact the composer refused to
// invent, standing in the letter as a placeholder. Two surfaces answer to it (the card's Kopieren
// and the bar's Genehmigen) and core answers to it too (it refuses an approval while one is open),
// so the count and its German live HERE rather than beside whichever surface needed them first.
// Read off the SERVED scaffold, deliberately: a fill is persisted the moment the clerk confirms
// it, so served state is the answer, and a surface deriving this from a local buffer would be
// telling the clerk about its own optimism rather than about the row.

/** How many of this slot's letter gaps are still open, as the server last served them. */
export function openSlotCount(slot: DraftSlot | null | undefined): number {
  const segments = slot?.scaffold?.segments;
  if (!segments) return 0;
  return segments.reduce(
    (open, segment) => open + (segment.kind === 'slot' && segment.status !== 'filled' ? 1 : 0), 0);
}

/** The ONE sentence naming what a letter is still missing. Never assembled at a call site. */
export function openSlotsNote(open: number): string {
  return `Noch ${open} ${open === 1 ? 'Feld' : 'Felder'} offen`;
}

// Core's machine marker for "this approval is refused because the letter still has gaps". A
// MARKER, not a sentence: the German it ships beside it is the server's own and is repeated
// verbatim, so matching on the marker cannot go stale when that wording is edited.
const SLOTS_OPEN_MARKER = 'slots_open';

/**
 * True for the shapes that refusal reaches this FE in — the claim envelope's `rejectReason` (the
 * action endpoint answers 200 and says in the body that it refused) and a raw 409 whose body
 * carries the marker. Same shape-tolerance, and the same reason for it, as
 * services/staleSuggestion's own predicate: a surface that recognised only one of them would be
 * right about half its callers.
 *
 * The remedy is a RELOAD, not a sentence: this surface's scaffold is behind the server's, and once
 * the row is refetched the slot refuses the press by itself, in the wording above.
 */
export function isSlotsOpenError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const envelope = err as { error?: unknown; resultState?: unknown };
  for (const field of [envelope.error, envelope.resultState]) {
    if (typeof field === 'string' && field.includes(SLOTS_OPEN_MARKER)) return true;
  }
  const response = (err as { response?: { status?: number; data?: unknown } }).response;
  if (!response || response.status !== 409) return false;
  const data = response.data;
  const body = typeof data === 'string'
    ? data
    : typeof (data as { message?: unknown })?.message === 'string'
      ? (data as { message: string }).message
      : '';
  return body.includes(SLOTS_OPEN_MARKER);
}
